/**
 * CourseCard Component
 * Netflix-style course card with hover effects
 */

import { useState, useEffect, useRef } from 'react';
import type { Course } from '../../types/academy';
import { Icons } from '../Icons';

interface CourseCardProps {
  course: Course;
  progress?: number; // 0-100 if user has started
  onPlay?: () => void;
  onAddToList?: () => void;
  onInfo?: () => void;
  isInList?: boolean;
}

export function CourseCard({
  course,
  progress,
  onPlay,
  onAddToList,
  onInfo,
  isInList = false
}: CourseCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [showExpandedInfo, setShowExpandedInfo] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);

  // Handle hover with delay for expanded info
  useEffect(() => {
    if (isHovered) {
      hoverTimeoutRef.current = window.setTimeout(() => {
        setShowExpandedInfo(true);
      }, 800); // 800ms delay before showing expanded info
    } else {
      if (hoverTimeoutRef.current !== null) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
      setShowExpandedInfo(false);
    }

    return () => {
      if (hoverTimeoutRef.current !== null) {
        window.clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isHovered]);

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'beginner': return '#10b981';
      case 'intermediate': return '#f59e0b';
      case 'advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getLevelEmoji = (level: string): string => {
    switch (level) {
      case 'beginner': return '🎓';
      case 'intermediate': return '📊';
      case 'advanced': return '🚀';
      default: return '📚';
    }
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`}>⭐</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half">⭐</span>);
    }
    return stars;
  };

  return (
    <div
      className="course-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        borderRadius: '8px',
        overflow: showExpandedInfo ? 'visible' : 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
        transform: isHovered ? 'scale(1.08)' : 'scale(1)',
        boxShadow: isHovered
          ? '0 12px 32px rgba(139, 92, 246, 0.5), 0 0 0 2px rgba(139, 92, 246, 0.3)'
          : 'none',
        zIndex: isHovered ? 10 : 1
      }}
    >
      {/* Thumbnail */}
      <div style={{ position: 'relative', paddingTop: '56.25%', background: '#1a1a1a' }}>
        <img
          src={course.thumbnail}
          alt={course.title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {/* Overlay on hover */}
        {isHovered && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.9) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '16px'
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlay?.();
              }}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'var(--accent)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <Icons.Play size={24} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToList?.();
              }}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid #fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
            >
              {isInList ? <Icons.Check size={18} /> : <Icons.Plus size={18} />}
            </button>

            {onInfo && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInfo();
                }}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  border: '2px solid #fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
              >
                <Icons.Info size={18} />
              </button>
            )}
          </div>
        )}

        {/* Badges */}
        <div style={{ position: 'absolute', top: '8px', left: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {course.isPro && (
            <div style={{
              padding: '4px 8px',
              borderRadius: '4px',
              background: 'rgba(139, 92, 246, 0.9)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              backdropFilter: 'blur(4px)'
            }}>
              PRO
            </div>
          )}
          {course.isFeatured && (
            <div style={{
              padding: '4px 8px',
              borderRadius: '4px',
              background: 'rgba(239, 68, 68, 0.9)',
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              backdropFilter: 'blur(4px)'
            }}>
              FEATURED
            </div>
          )}
        </div>

        {/* Progress bar */}
        {progress !== undefined && progress > 0 && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'rgba(255,255,255,0.2)'
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: 'var(--accent)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '12px', background: '#1a1a1a' }}>
        <h3 style={{
          fontSize: '15px',
          fontWeight: 600,
          marginBottom: '6px',
          lineHeight: '1.3',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {course.title}
        </h3>

        <div style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '8px' }}>
          {course.instructorAvatar && <span style={{ marginRight: '4px' }}>{course.instructorAvatar}</span>}
          {course.instructor}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '2px' }}>
            {renderStars(course.rating)}
            <span style={{ marginLeft: '4px', color: 'var(--muted)' }}>{course.rating.toFixed(1)}</span>
          </div>
          <span style={{ color: 'var(--muted)' }}>•</span>
          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {course.duration}m
          </div>
          {course.lessons && (
            <>
              <span style={{ color: 'var(--muted)' }}>•</span>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                {course.lessons} lessons
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            padding: '4px 10px',
            borderRadius: '6px',
            background: getLevelColor(course.level),
            color: '#fff',
            fontSize: '11px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {getLevelEmoji(course.level)}
            {course.level}
          </span>

          {progress !== undefined && progress > 0 && (
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {progress}% complete
            </span>
          )}
        </div>
      </div>

      {/* Expanded Info Panel (appears after 800ms hover) */}
      {showExpandedInfo && course.whatYouLearn && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '16px',
            zIndex: 20,
            animation: 'expandPanel 0.3s ease-out',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h4 style={{
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '10px',
            color: 'var(--accent)'
          }}>
            What You'll Learn:
          </h4>
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px'
          }}>
            {course.whatYouLearn.slice(0, 3).map((item, index) => (
              <li
                key={index}
                style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px'
                }}
              >
                <span style={{ color: 'var(--accent)', marginTop: '2px' }}>✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Animation for expanded panel */}
      <style>{`
        @keyframes expandPanel {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
